<template>
    <div id="contents" class="contents spirit-of-japan">
        <section class="main">
            <div class="texts">
                <div class="inner">
                    <p class="sub">A Journey to the True Spirit of Japan</p>
                    <h2>Ancient Paths<br>
                        Where Japanese Culture Dwells</h2>
                    <p class="lead">
                        Around the ancient paths that were once bustling thoroughfares, new forms of culture were ceaselessly being born.<br>
                        <br>
                        From the handicrafts created by artisans amid forest conservation policy cherished by travelers to the cobblestone streets serving as arteries to the nation’s heart, Edo (modern-day Tokyo) flanked by inns and tea houses. A path traversing three mountains that became synonymous with ascetic training aimed at spiritual rebirth, and a pilgrim trail stretching over 1000 kilometers renown for the profound hospitality offered by local residents.<br>
                        <br>
                        These ancient paths that criss-cross the nation are more than mere remnants of former transportation means. They are in fact embodiments of the very souls of Japanese people.
                        Here, we will introduce various Japanese culture conceived from these paths.
                    </p>
                </div>
            </div>
            <div class="img"><img src="../../assets/spirit_of_japan6/main01.webp" alt="" class="_pc"><img src="../../assets/spirit_of_japan6/main01_sp.webp" alt="" class="_sp"></div>
        </section>
        <section class="basic-section">
            <div class="para -reverse">
                <div class="img"><img src="../../assets/spirit_of_japan6/img01.webp" alt=""></div>
                <div class="txt">
                    <h3>Traditional Wooden Crafts From Nakasendo Road Recognized Around the World</h3>
                    <p>
                        Situated in Nagano prefecture’s southwest, the vast majority of the Kiso region is blanketed in thick forest, making it renowned for its premium-quality timber. “Nakasendo” is the trail that passes through Kiso connecting Edo (present-day Tokyo) and Kyoto (Japan’s ancient capital). During the Edo period (1603 – 1867), the surge in demand for hinoki cypress to build castles very nearly resulted in the depletion of Kiso’s forests.<br>
                        In response, the Owari domain lord who ruled the region declared the strict punishment of “one tree, one head,” suggesting execution as part of an extreme forest conservation policy forbidding the logging of high-quality timber.<br>
                        In order to make a living amid such stringent restrictions, the locals honed their artistic skills and used only permitted timber to create the intricate crafts of “Magemono,” “Kiso Lacquerware,” and “Orokugushi combs” that remain icons of Kiso to this day.<br>
                        Such items were highly-sought by travelers along Nakasendo during this era, to the point where people even travelled to Kiso for the sole purpose of purchasing these coveted souvenirs, as portrayed in Utagawa Hiroshige's ukiyo-e print “Narai” and popular travel tales of the time.<br>
                        Today, visitors to Kiso can still enjoy the atmosphere of this former post town, which has been preserved in its original form. The stretch from Magome to Tsumago in particular draws people from all over the world as a hiking route offering authentic Japanese scenery where one can enjoy rows of traditional inns and tea houses dating back to the Edo period.<br>
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: '9d12475a-36a2-4c49-bdf2-b95c3f680105', page: 'about', url: 'preserving-mountain-forests-kiso-nagano-Kiso-Goboku-Tsumago-juku'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para">
                <div class="img"><img src="../../assets/spirit_of_japan6/img02.webp" alt=""></div>
                <div class="txt">
                    <h3>Hakone Hachiri – The Perilous Mountain Trail Preserving the Footprints of Historical Figures</h3>
                    <p>
                        Along the Old Tokaido Road, one of the ancient arteries connecting Edo (present-day Tokyo) and the ancient capital of Kyoto, is a stretch known as “Hakone Hachiri,” reputed as a treacherous section of the journey due to its steep terrain and stringent checkpoint.<br>
                        This part of the trail is approximately 32 kilometers in length and weaves through a steep mountain pass over 800 meters above sea level. Initially, the trail was in such poor condition that those who met with rainy conditions had to wade knee-deep through mud. To address this issue and ensure the smooth flow of goods and people vital to Edo's prosperity, the shogunate government undertook a massive project to pave the road with stone, thus making it resilient to inclement weather.<br>
                        This stone-paved trail was not merely a means of transportation, but also a main thoroughfare for diplomacy and politics. From the elaborate and highly-ceremonial processions of “Daimyo of the West” commanding hundreds to thousands of retainers, to international delegations like the Dutch trading post chief and the Korean envoys, many passed along this path, each with their own unique destiny.<br>
                        A highlight of present-day Hakone Hachiri is Hakone Checkpoint, where Japan's strictest border controls were once enforced. The checkpoint was fully restored in 2007, meticulously adhering to 400-year-old techniques.<br>
                        Another must-see highlight is Amasake Chaya – a traditional tea house that has been providing weary travelers with respite for 13 generations ceaselessly since the Edo period. We invite you to take a walk back in time and admire the untouched scenery of the ancient journey to Edo.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: 'd5dbf952-af1c-4a9d-b5a5-f853b1ef14d1', page: 'about', url:'perilous-mountain-pass-kyoto-tokyo-highway-shizuoka-kanagawa'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para -reverse">
                <div class="img"><img src="../../assets/spirit_of_japan6/img03.webp" alt=""></div>
                <div class="txt">
                    <h3>A Journey of Rebirth Traversing Three Moutains</h3>
                    <p>
                        Mount Haguro, Mount Gassan, and Mount Yudono are three mountains situated in the center of Yamagata prefecture collectively known as “Dewa Sanzan.” These mountains are considered sacred by practitioners of Shugendo, a religion that blends Buddhist beliefs with the ancient Japanese practice of mountain worship.<br>
                        Since the Edo period, people have believed that completing a pilgrimage of all three revered mountains was equivalent to a transformative journey of death, soul purification, and ultimately, rebirth.<br>
                        The first mountain of the journey, Mount Haguro, symbolizes the present. It is here that worshippers pray for rewards in this life.<br>
                        The mountain is also home to centuries-old cedar trees awarded three stars in Michelin Green Guide Japan. One step among these trees and you will instantly forget about the bustle of the daily grind, gaining a deep sense of serenity in its place. Climbing no less than 2446 stone steps one at a time, the rhythm as you ascend will erase all distracting thoughts at some point, bringing an overwhelming sense of immersion as you seemingly merge with the surrounding nature.<br>
                        The second mountain of the journey is Mount Gassan, which represents the past. This is where pilgrims pray for peace in the afterlife. The third mountain is Mount Yudono, which represents the future and is where the pilgrim will be granted his or her new life and return to the real world.<br>
                        In close proximity to the Dewa Sanzan trails are a number of “shukubo,” temple lodgings run by the Shugendo yamabushi monks who devote themselves to their faith dressed entirely in white.<br>
                        An overnight stay at a shukubo offers a unique cultural experience featuring nourishing vegetarian meals called “shojin ryori,” which is believed to have the effect of “detoxing the soul” from within.<br>
                        This journey is much more than a simple hike through the mountains. It is a precious opportunity to achieve a spiritual transformation through genuine self-reflection and ultimately gain a sense of being reborn with the strength to face the future.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: '8ad99af4-1a76-408b-9bf8-6fb90fb3e3e0', page: 'about', url: 'dewa-sanzan-rebirth-cedars-yamagata'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para">
                <div class="img"><img src="../../assets/spirit_of_japan6/img04.webp" alt=""></div>
                <div class="txt">
                    <h3>The Shikoku Pilgrimage – Japan’s Foremost Long-Distance Circular Pilgrim Route</h3>
                    <p>
                        The island of Shikoku is the birthplace of Kukai (774 – 835), a Buddhist monk and scholar hailed in Japanese history as a “giant of religion and culture.” The Shikoku Pilgrimage is a grand spiritual journey of around 1400 kilometers involving stops at 88 temples. Even today, over a thousand years on, countless “ohenro” (pilgrims) walk this trail following in Kukai’s footsteps.<br>
                        The 88 temples that comprise this journey belong to a diversity of sects including Shingon, Tendai, and Zen, but they are all deeply connected purely because they share a common purpose: to serve as guardians of Kukai’s sacred sites.<br>
                        This sense of inclusiveness transcending the conventional boundaries of religion forms the very foundation of the pilgrimage culture, which welcomes all people regardless of faith or nationality as “ohenro traveling the same path as Kukai.”<br>
                        This area is renowned for its unique tradition of “osettai,” whereby locals display immense hospitality towards ohenro on their journey. Based on the belief that the soul of Kukai is in fact accompanying these pilgrims, the locals show the utmost respect as though the ohenro are reincarnations of Kukai himself; offering hospitality such as food, drinks, and more.  Osettai is a way for locals and pilgrams to connect to Kukai as an expression of mutual aid whereby they share the merits of spiritual practice.
                    </p>
                    <p>
                        For over 1200 years, the people of Shikoku have walked side-by-side the ohenro. This is a form of unconditional hospitality whereby complete strangers unhesitantly support a person’s spiritual journey. An emotional connection that makes one can feel like they are not alone. The opportunity to experience this firsthand has inspired people from all over the world and earned the Shikoku Pilgrimage a reputation as the “ultimate journey of rebirth.”
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: '6505c8e1-f464-4901-8cd7-f43ec4eaf6f7', page: 'about', url: 'henro-spiritual-shikoku-tokushima-kochi-ehime-kagawa'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    <section class="section03">
            <div class="inner">
                <h2>Other Articles</h2>
                <ul>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan1'}">
                            <div class="img"><img src="../../assets/spirit_of_japan/other.jpg" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Discover Craftsmanship Unbowed by Time</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan2'}">
                            <div class="img"><img src="../../assets/spirit_of_japan2/other.jpg" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Sacred Sites where Nature and Faith Blend into One</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan3'}">
                            <div class="img"><img src="../../assets/spirit_of_japan3/other.png" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Unlocking the Secrets of the Shogun, Samurai and Ninja</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan4'}">
                            <div class="img"><img src="../../assets/spirit_of_japan4/other.webp" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">The Journey of Japan's
                                    Original Landscapes Shaped by Water</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan5'}">
                            <div class="img"><img src="../../assets/spirit_of_japan5/other.webp" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Encounter Festivals
                                    Embodying the Japanese Spirit</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan7'}">
                            <div class="img"><img src="../../assets/spirit_of_japan7/main01.webp" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">“O-sake” — the drink that nurtured Japanese history</div>
                            </div>
                        </router-link>
                    </li>
                </ul>
            </div>
        </section>
    </div>
</template>

<script>
import ApiRouter from "../../components/Systems/ApiRouter";

export default {
    name: "PlanYourVisit",
    mixins: [ApiRouter],
    data: function () {
        return {
            articles: [],
            activeTabMenu: 'half'
        }
    },
    created: function () {
        this.loadData();
        this.getQuery();
        document.title = 'Spirit Of Japan | Japan Heritage Official Site'
    },
    methods: {
        loadData: function () {
            window.axios.get(this.routes.articles)
                .then((response) => {
                    this.articles = response.data.data;
                });
        },
        getQuery: function () {
            const tab = this.$route.query.tab;

            switch (tab) {
                case 'day1':
                    this.activeTabMenu = 'day1';
                    break;
                case 'day2':
                    this.activeTabMenu = 'day2';
                    break;
                case 'day3':
                    this.activeTabMenu = 'day3';
                    break;
                default:
                    this.activeTabMenu = 'half';
                    break;
            }
        },
        articleCardHasClicked: function (id, hash, breadcrumb) {
            this.$router.push({name: 'Popular', params: {id: id, page: 'travelInfo', breadcrumb: breadcrumb}, hash: hash});
        }
    },
    watch: {
        $route() {
            this.getQuery();
        }
    }
}
</script>

<style src="./SpiritOfJapan6.scss" lang="scss" scoped />
